import { useEffect, useRef, useState } from "react";
import {
  groupAndSortWallets,
  useWallet,
} from "@aptos-labs/wallet-adapter-react";
import { BkfcDrawer } from "./BkfcDrawer";
import { isLikelyEmbeddedWebview } from "../embeddedWebview";

// "Connect to claim" drawer (Figma 3608:628): social login (Aptos Connect /
// "Petra Web" → Google keyless wallet) or a native Aptos wallet. Connection
// mechanics ported from the reference app's ConnectPanel — connect() is void
// (errors surface via the provider's onError), social login is blocked inside
// embedded webviews, and a stalled social login is flagged after 30s.
const CONNECT_STALL_MS = 30_000;

type Selection =
  | { type: "google" }
  | { type: "native"; walletName?: string }
  | null;

interface Props {
  onClose: () => void;
  onConnected: () => void;
}

export function BkfcConnectDrawer({ onClose, onConnected }: Props) {
  const {
    connect,
    disconnect,
    connected,
    account,
    wallet,
    isLoading,
    wallets,
    notDetectedWallets,
  } = useWallet();

  const [selection, setSelection] = useState<Selection>(null);
  const [webviewWarning, setWebviewWarning] = useState<
    "detected" | "connect-stalled" | null
  >(isLikelyEmbeddedWebview() ? "detected" : null);
  const stallTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasConnectedOnOpen = useRef(connected);

  // Aptos Connect got renamed "Petra Web" in wallet-adapter-core 8.x;
  // petraWebWallets is the social-login group.
  const { petraWebWallets, availableWallets, installableWallets } =
    groupAndSortWallets([...wallets, ...notDetectedWallets]);

  const googleWallet =
    petraWebWallets.find((w) => /google/i.test(w.name)) ?? petraWebWallets[0];

  // Watch isLoading, not connected: a real rejection also leaves connected
  // false, but isLoading settles either way, so only a genuinely stuck
  // attempt trips the stall banner.
  useEffect(() => {
    if (!isLoading) {
      if (stallTimer.current) {
        clearTimeout(stallTimer.current);
        stallTimer.current = null;
      }
      setWebviewWarning((prev) => (prev === "connect-stalled" ? null : prev));
    }
  }, [isLoading]);

  useEffect(() => {
    return () => {
      if (stallTimer.current) clearTimeout(stallTimer.current);
    };
  }, []);

  // fire the parent callback once a fresh connection lands
  useEffect(() => {
    if (connected && !wasConnectedOnOpen.current) {
      onConnected();
    }
  }, [connected, onConnected]);

  const handleConnectNow = () => {
    if (!selection) return;

    if (selection.type === "google") {
      if (!googleWallet) return;
      stallTimer.current = setTimeout(
        () => setWebviewWarning("connect-stalled"),
        CONNECT_STALL_MS,
      );
      try {
        connect(googleWallet.name); // void, not a Promise
      } catch (err) {
        console.error("BKFC connect error:", err);
      }
      return;
    }

    const targetName =
      selection.walletName ??
      (availableWallets.length === 1 ? availableWallets[0].name : undefined);
    if (!targetName) return;
    try {
      connect(targetName);
    } catch (err) {
      console.error("BKFC connect error:", err);
    }
  };

  const nativeNames =
    availableWallets.length > 0
      ? availableWallets.map((w) => w.name).join(", ")
      : "Petra, Martian, Pontem";

  const canConnect =
    !!selection &&
    (selection.type === "google"
      ? !!googleWallet
      : availableWallets.length === 1 || !!selection.walletName);

  // Already connected: offer continue / disconnect instead of the options.
  if (connected && wasConnectedOnOpen.current) {
    const address = account?.address?.toString() ?? "";
    return (
      <BkfcDrawer onClose={onClose}>
        <div className="text-[23px] font-bold text-black">Wallet connected</div>
        <div className="mt-2.5 text-lg text-[#626262] break-all">
          {wallet?.name} · {address.slice(0, 6)}...{address.slice(-4)}
        </div>
        <button
          onClick={onConnected}
          className="mt-6 w-full h-10 font-bold text-white bg-[#1F1F1F] transition-colors hover:bg-gray-800"
        >
          Continue
        </button>
        <button
          onClick={() => disconnect()}
          className="mt-2.5 w-full h-10 font-bold text-black border-2 border-black transition-colors hover:bg-gray-100"
        >
          Disconnect
        </button>
      </BkfcDrawer>
    );
  }

  return (
    <BkfcDrawer onClose={onClose}>
      <div className="text-[23px] font-bold text-black">Connect to claim</div>

      <div className="mt-2.5 text-lg text-[#626262]">
        Your certificate is minted to a wallet. Gas is sponsored - you need no
        APT.
      </div>

      {webviewWarning && (
        <div className="mt-3 px-4 py-3 text-sm text-amber-800 bg-amber-50 rounded border border-amber-300">
          {webviewWarning === "detected"
            ? "You appear to be in an in-app browser. Google login may not work here — open this page in Safari or Chrome, or use a native Aptos wallet."
            : "This login is taking a while. If nothing happens, open this page in Safari or Chrome and try again."}
        </div>
      )}

      <div className="mt-4 pb-4 flex flex-col gap-2">
        <button
          onClick={() => setSelection({ type: "google" })}
          className={`flex items-center justify-between px-4 py-3.5 bg-[#F8F8F8] w-full text-left border ${
            selection?.type === "google"
              ? "border-black"
              : "border-transparent"
          }`}
        >
          <span className="flex flex-col gap-1.5">
            <span className="text-base text-black">Continue with Google</span>
            <span className="text-sm text-[#626262]">
              Creates a wallet for you
            </span>
          </span>
          <span className="w-8 h-8 rounded-full overflow-hidden shrink-0 flex items-center justify-center">
            {/* zoomed ~119% like the Figma node, so the G fills the disc */}
            <img
              src="/svg/bkfc/google-logo.png"
              alt="Google"
              className="w-full h-full object-cover scale-[1.19]"
            />
          </span>
        </button>

        <button
          onClick={() => setSelection({ type: "native" })}
          className={`flex items-center justify-between px-4 py-3.5 bg-[#F8F8F8] w-full text-left border ${
            selection?.type === "native"
              ? "border-black"
              : "border-transparent"
          }`}
        >
          <span className="flex flex-col gap-1.5">
            <span className="text-base text-black">Aptos wallet</span>
            <span className="text-sm text-[#626262]">{nativeNames}</span>
          </span>
          <img
            src="/svg/bkfc/aptos-logo.png"
            alt="Aptos"
            className="w-8 h-8 rounded-full object-cover"
          />
        </button>

        {/* multiple detected native wallets → pick one */}
        {selection?.type === "native" && availableWallets.length > 1 && (
          <div className="flex flex-col gap-1.5 pl-4">
            {availableWallets.map((w) => (
              <button
                key={w.name}
                onClick={() =>
                  setSelection({ type: "native", walletName: w.name })
                }
                className={`flex items-center gap-2.5 px-4 py-2.5 bg-[#F8F8F8] w-full text-left border ${
                  selection.walletName === w.name
                    ? "border-black"
                    : "border-transparent"
                }`}
              >
                {w.icon && (
                  <img src={w.icon} alt="" className="w-6 h-6 rounded-full" />
                )}
                <span className="text-sm text-black">{w.name}</span>
              </button>
            ))}
          </div>
        )}

        {/* no native wallet detected → install links */}
        {selection?.type === "native" && availableWallets.length === 0 && (
          <div className="flex flex-col gap-1.5 pl-4">
            {installableWallets.slice(0, 3).map((w) => (
              <a
                key={w.name}
                href={w.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2.5 px-4 py-2.5 bg-[#F8F8F8] w-full text-sm text-black underline"
              >
                Install {w.name}
              </a>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={handleConnectNow}
        disabled={!canConnect || isLoading}
        className="w-full h-10 font-bold text-white bg-[#1F1F1F] transition-colors hover:bg-gray-800 disabled:bg-[#B4B4B4] disabled:cursor-not-allowed"
      >
        {isLoading ? "Connecting..." : "Connect now"}
      </button>
    </BkfcDrawer>
  );
}
